import { useMemo } from "react";

import DOMPurify from "dompurify";

interface IUseMarkdownProcessorProps {
    text: string;
    highlightedPhrase?: RegExp | string | string[];
    caseSensitive?: boolean;
}

const ALLOWED_MARKDOWN_TAGS = ["a", "blockquote", "code", "del", "h1", "h2", "h3", "h4", "hr", "span", "strong", "sup"];
const ALLOWED_MARKDOWN_ATTRIBUTES = ["class", "href", "rel", "target"];
const MARKDOWN_SANITIZE_CONFIG = {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- DOMPurify uses uppercase option names
    ALLOWED_ATTR: ALLOWED_MARKDOWN_ATTRIBUTES,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- DOMPurify uses uppercase option names
    ALLOWED_TAGS: ALLOWED_MARKDOWN_TAGS,
};

export const useMarkdownProcessor = ({
    text,
    highlightedPhrase = "",
    caseSensitive = false,
}: IUseMarkdownProcessorProps): string => {
    const cleanPhrase = (phrase: string): string => {
        return phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };

    const regex = useMemo<RegExp | null>(() => {
        if (!highlightedPhrase) {
            return null;
        }

        const flags = caseSensitive ? "g" : "gi";

        if (Array.isArray(highlightedPhrase)) {
            /*
                Sort by length (longest first) because we want the highlighter to look for the longest match first,
                and not settle for a shorter match in the middle of the phrase.
                
                E.g:
                If we are looking for the words "Adobe" and "Adobe Illustrator",
                then when we meet the phrase "Adobe Illustrator"
                we want it to highlight the whole thing and not just "Adobe".
            */
            const sortedPhrases = highlightedPhrase
                .filter((phrase) => phrase)
                .sort((a, b) => b.length - a.length)
                .map((phrase) => cleanPhrase(phrase));

            if (!sortedPhrases?.length) {
                return null;
            }

            return new RegExp(`(${sortedPhrases.join("|")})`, flags);
        } else if (typeof highlightedPhrase === "string") {
            const phrase = cleanPhrase(highlightedPhrase);
            return new RegExp(`(${phrase})`, flags);
        } else if (highlightedPhrase instanceof RegExp) {
            const pattern = highlightedPhrase.source;
            const regexFlags = highlightedPhrase.flags;
            return new RegExp(`(${pattern})`, regexFlags);
        }

        return null;
    }, [highlightedPhrase, caseSensitive]);

    return useMemo<string>(() => {
        let result = text.replace(/\\\./g, ".").replace(/\\\[(\d+)\\]/g, "[$1]");

        if (regex) {
            result = result.replace(regex, '<span class="sw-highlight-marker">$1</span>');
        }

        const html = result
            .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
            .replace(/~~(.*?)~~/g, "<del>$1</del>")
            .replace(/`([^`]+)`/g, "<code>$1</code>")
            .replace(/^#### (.+)$/gm, "<h4>$1</h4>")
            .replace(/^### (.+)$/gm, "<h3>$1</h3>")
            .replace(/^## (.+)$/gm, "<h2>$1</h2>")
            .replace(/^# (.+)$/gm, "<h1>$1</h1>")
            .replace(/^---$/gm, "<hr>")
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
            .replace(/^> (.+)$/gm, "<blockquote>$1</blockquote>")
            .replace(/\[(\d+)\]/g, "<sup>[$1]</sup>");

        return DOMPurify.sanitize(html, MARKDOWN_SANITIZE_CONFIG);
    }, [text, regex]);
};
